import React from "react";
import ReactDOM from "react-dom/client";
import { registerSW } from "virtual:pwa-register";
import App from "./App";

function showBootError(message: string): void {
  const pre = document.createElement("pre");
  pre.textContent = message;
  pre.style.position = "fixed";
  pre.style.inset = "0";
  pre.style.margin = "0";
  pre.style.padding = "16px";
  pre.style.overflow = "auto";
  pre.style.background = "#111827";
  pre.style.color = "#f9fafb";
  pre.style.fontFamily = "Consolas, monospace";
  pre.style.fontSize = "13px";
  pre.style.zIndex = "2147483647";
  document.body.appendChild(pre);
}

let appRendered = false;

const isAbortError = (reason: unknown): boolean =>
  reason instanceof DOMException
    ? reason.name === "AbortError"
    : reason instanceof Error && reason.name === "AbortError";

const describeReason = (reason: unknown): string => {
  if (reason instanceof Error) {
    return reason.stack || reason.message;
  }
  if (typeof reason === "string") {
    return reason;
  }
  try {
    return JSON.stringify(reason, null, 2) || String(reason);
  } catch {
    return String(reason);
  }
};

window.addEventListener("error", (event) => {
  if (appRendered) {
    return;
  }
  const location = `${event.filename}:${event.lineno}:${event.colno}`;
  showBootError(`SkyView startup error:\n${event.message}\n${location}`);
});

window.addEventListener("unhandledrejection", (event) => {
  if (appRendered || isAbortError(event.reason)) {
    return;
  }
  showBootError(`SkyView startup rejection:\n${describeReason(event.reason)}`);
});

const userAgent = typeof navigator !== "undefined" ? navigator.userAgent : "";
const hasWailsRuntime =
  typeof (window as { runtime?: unknown }).runtime !== "undefined" ||
  typeof (window as { go?: unknown }).go !== "undefined" ||
  typeof (window as { __WAILS__?: unknown }).__WAILS__ !== "undefined";
const isDesktopShell =
  hasWailsRuntime ||
  /wails|webview2|;\s*wv\)/i.test(userAgent) ||
  window.location.hostname === "wails.localhost";

const DESKTOP_NO_SW_PARAM = "_sv_no_sw";

const disableDesktopPwa = async (): Promise<boolean> => {
  if (!isDesktopShell || typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
    return false;
  }

  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((registration) => registration.unregister()));
  } catch {
    // Ignore unregister errors and continue with cache cleanup.
  }

  if (typeof caches !== "undefined") {
    try {
      const keys = await caches.keys();
      await Promise.all(keys.map((key) => caches.delete(key)));
    } catch {
      // Ignore cache cleanup errors in desktop shell mode.
    }
  }

  if (navigator.serviceWorker.controller) {
    const nextUrl = new URL(window.location.href);
    if (!nextUrl.searchParams.has(DESKTOP_NO_SW_PARAM)) {
      nextUrl.searchParams.set(DESKTOP_NO_SW_PARAM, "1");
      window.location.replace(nextUrl.toString());
      return true;
    }
  }

  return false;
};

const clearDesktopNoSwQueryParam = () => {
  const current = new URL(window.location.href);
  if (!current.searchParams.has(DESKTOP_NO_SW_PARAM)) {
    return;
  }
  current.searchParams.delete(DESKTOP_NO_SW_PARAM);
  const query = current.searchParams.toString();
  const nextUrl = `${current.pathname}${query ? `?${query}` : ""}${current.hash}`;
  window.history.replaceState(null, "", nextUrl);
};

if (isDesktopShell) {
  window.addEventListener("keydown", (event: KeyboardEvent) => {
    if (event.key !== "F5") {
      return;
    }
    event.preventDefault();
    window.location.reload();
  });
}

const startApp = async () => {
  if (await disableDesktopPwa()) {
    return;
  }

  if (isDesktopShell) {
    clearDesktopNoSwQueryParam();
  }

  try {
    ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
      <App />
    );
    appRendered = true;
  } catch (error) {
    const message = error instanceof Error ? error.stack || error.message : String(error);
    showBootError(`SkyView failed to render:\n${message}`);
  }

  if (!isDesktopShell) {
    const updateSW = registerSW({
      immediate: true,
      onNeedRefresh() {
        console.info("SkyView update detected. Reloading to apply the latest version.");
        updateSW(true);
      },
      onOfflineReady() {
        console.info("SkyView is ready to be used offline.");
      }
    });
  }
};

void startApp();
